import * as React from 'react'
import classNames from 'classnames'
import { Image as ImageIcon } from 'lucide-react'
import { useTranslation } from 'translations'
import { Locale } from 'admin/articleService'
import { ImageUploadResponse } from 'http/imagesService'
import { MessageKind } from 'uiComponents/messages'
import { SingleSelect, SingleSelectOption, TextInput } from 'uiComponents/input'
import { UploadInput } from 'uiComponents/input/upload'
import { SegmentedControl } from 'uiComponents/segmentedControl/segmentedControl'
import { GoogleListingItem, Translation } from 'channels/googleTTDService'
import { ImageSize, MIN_IMAGE_SIZE } from '../../model/readiness'
import { CategoryChips } from '../categoryChips/categoryChips'
import './venueSection.scss'

interface VenueSectionProps {
    item: GoogleListingItem
    venueName: string
    /** Venues not yet listed, plus this one; a select shows only with two or more. */
    venueOptions: SingleSelectOption[]
    languages: Locale[]
    imageSize: ImageSize | null
    markErrors: boolean
    canEdit: boolean
    onVenueChange: (locationId: string) => void
    onPlaceIdChange: (placeId: string) => void
    onCategoryChange: (categories: string[]) => void
    onDescriptionChange: (text: string, language: string) => void
    onImageUpload: (file: File) => Promise<any>
    onImageUploadSuccess: (response: ImageUploadResponse) => void
    onRemoveImage: () => void
    replaceMessages: (id: string, status: MessageKind, text: string) => void
    removeAllMessages: () => void
}

const textFor = (translations: Translation[], language: string) =>
    translations.find((translation) => translation.language === language)?.text ?? ''

/** The venue half of a listing: where it is on Google, what it is, how it reads and looks. */
export function VenueSection(props: VenueSectionProps) {
    const { t } = useTranslation()
    const { item, languages } = props
    const [language, setLanguage] = React.useState<string>(languages[0]?.code ?? '')
    const placeError = props.markErrors && !item.googlePlaceId.trim()
    const descriptionMissing = (code: string) => !textFor(item.description, code).trim()
    const descriptionError = props.markErrors && descriptionMissing(language)
    const imageTooSmall =
        !!item.image &&
        !!props.imageSize &&
        (props.imageSize.width < MIN_IMAGE_SIZE || props.imageSize.width !== props.imageSize.height)

    React.useEffect(() => {
        if (props.markErrors) {
            const missing = languages.find((locale) => descriptionMissing(locale.code))
            if (missing) setLanguage(missing.code)
        }
    }, [props.markErrors])

    const languageOptions = languages.map((locale) => ({ value: locale.code, label: locale.name }))

    return (
        <div className="venue-section">
            <div className="venue-column">
                <div className="venue-field" id="gttd-field-place">
                    <label className="venue-label" htmlFor={`venue-${item.id}`}>
                        {t('channels.googleTTD.venue.location')}
                    </label>
                    {props.venueOptions.length > 1 ? (
                        <SingleSelect
                            id={`venue-${item.id}`}
                            options={props.venueOptions}
                            selected={item.locationId}
                            height="2.5rem"
                            noSelectOption={t('channels.googleTTD.venue.noVenue')}
                            onSelect={props.onVenueChange}
                            disabled={!props.canEdit}
                        />
                    ) : (
                        <TextInput id={`venue-${item.id}`} value={props.venueName} disabled block />
                    )}
                </div>
                <div className="venue-field">
                    <label className="venue-label" htmlFor={`place-${item.id}`}>
                        {t('channels.googleTTD.venue.placeId')}
                    </label>
                    <TextInput
                        id={`place-${item.id}`}
                        value={item.googlePlaceId}
                        placeholder={t('channels.googleTTD.venue.idPlaceholder')}
                        status={placeError ? 'error' : 'normal'}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            props.onPlaceIdChange(event.target.value)
                        }
                        disabled={!props.canEdit}
                        block
                    />
                    <p className="venue-hint">{t('channels.googleTTD.venue.placeIdHint')}</p>
                </div>
                <div className="venue-field" id="gttd-field-categories">
                    <span className="venue-label" id={`categories-${item.id}`}>
                        {t('channels.googleTTD.venue.categories')}
                    </span>
                    <CategoryChips
                        selected={item.options[0]?.category ?? []}
                        onChange={props.onCategoryChange}
                        disabled={!props.canEdit}
                        markError={props.markErrors}
                        ariaLabelledBy={`categories-${item.id}`}
                    />
                    <p className="venue-hint">{t('channels.googleTTD.venue.categoriesHint')}</p>
                </div>
            </div>
            <div className="venue-column">
                <div className="venue-field" id="gttd-field-description">
                    <div className="venue-field-head">
                        <label className="venue-label" htmlFor={`description-${item.id}-${language}`}>
                            {t('channels.googleTTD.venue.description')}
                        </label>
                        {languages.length > 1 && (
                            <SegmentedControl
                                options={languageOptions}
                                value={language}
                                onChange={setLanguage}
                                ariaLabel={t('channels.googleTTD.venue.translations')}
                                size="compact"
                            />
                        )}
                    </div>
                    <textarea
                        id={`description-${item.id}-${language}`}
                        className={classNames('venue-textarea', { 'has-error': descriptionError })}
                        value={textFor(item.description, language)}
                        maxLength={10000}
                        placeholder={t('channels.googleTTD.venue.descriptionPlaceholder')}
                        onChange={(event) => props.onDescriptionChange(event.target.value, language)}
                        disabled={!props.canEdit}
                        aria-invalid={descriptionError}
                    />
                    <p className={classNames('venue-hint', { 'is-error': descriptionError })}>
                        {descriptionError
                            ? t('channels.googleTTD.venue.descriptionMissing', {
                                  language: languages.find((locale) => locale.code === language)?.name ?? language,
                              })
                            : t('channels.googleTTD.venue.descriptionHint')}
                    </p>
                </div>
                <div className="venue-field" id="gttd-field-image">
                    <label className="venue-label" htmlFor={`venue-image-${item.id}`}>
                        {t('channels.googleTTD.venue.image')}
                    </label>
                    <div className="venue-image-row">
                        <div
                            className={classNames('venue-thumb', {
                                'has-error': imageTooSmall || (props.markErrors && !item.image),
                            })}
                        >
                            {item.image ? (
                                <>
                                    <img src={item.image} alt="" />
                                    {props.imageSize && (
                                        <span className="venue-thumb-size">
                                            {`${props.imageSize.width}×${props.imageSize.height}`}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <span className="venue-thumb-empty">
                                    <ImageIcon size={20} aria-hidden="true" />
                                    {t('channels.googleTTD.venue.noImage')}
                                </span>
                            )}
                        </div>
                        <div className="venue-image-actions">
                            {props.canEdit && (
                                <UploadInput
                                    type="img"
                                    fileSizeLimit={5}
                                    onUpload={props.onImageUpload}
                                    handleUploadSuccessResponse={props.onImageUploadSuccess}
                                    inputId={`venue-image-${item.id}`}
                                    parseErrorMessage
                                    replaceTopMessages={props.replaceMessages}
                                    removeAllMessages={props.removeAllMessages}
                                />
                            )}
                            {item.image && props.canEdit && (
                                <button type="button" className="venue-remove-image" onClick={props.onRemoveImage}>
                                    {t('channels.googleTTD.venue.removeImage')}
                                </button>
                            )}
                        </div>
                    </div>
                    <p className={classNames('venue-hint', { 'is-error': imageTooSmall })}>
                        {imageTooSmall
                            ? t('channels.googleTTD.venue.imageTooSmall', { size: MIN_IMAGE_SIZE })
                            : t('channels.googleTTD.venue.imageHint', { size: MIN_IMAGE_SIZE })}
                    </p>
                </div>
            </div>
        </div>
    )
}
